import type { ReactNode } from 'react';
import { firstPendingLeg, isTransfer, type LegField, legsComplete } from './leg_state.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import { PLANNER_MAX_LEGS, type PlannerLeg } from './planner_models.js';
import type { SearchSelectOption, SearchSelectSection } from './place_option.js';
import type { PlaceGuide, PlannerGuide } from './guided_fill.js';
import { RouteField } from './route_field.js';
import { whenMirrorClass } from './when_trigger.js';
import { fmtWeekdayDayMonth } from './planner_dates.js';
import {
  AirplaneLandingIcon,
  AirplaneTakeoffIcon,
  ArrowRightIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import './flight_planner.css';

/** Sugestões pré-digitação por ponta do trecho (ex.: "Recentes"). Ausentes ⇒ o campo
 *  aberto pede "Digite para buscar", como antes. A MESMA lista das duas pontas da
 *  rota binária: um trecho do multi é uma rota, e o histórico é do viajante, não do
 *  modo. */
type LegAirportSuggestions = {
  origin?: SearchSelectSection[];
  destination?: SearchSelectSection[];
};

/**
 * O número do trecho, em bolha, com a METADE do fio que liga na bolha vizinha.
 *
 * O fio transforma N linhas independentes numa SEQUÊNCIA, que é o que o
 * multi-trecho é — a origem de um é o destino do anterior. Cada linha desenhar
 * metade é o que sobrevive às duas alturas de linha: a aberta é mais alta que a
 * colapsada, e um fio de altura fixa descolaria. Cada junta tem UM dono: a metade de
 * baixo atravessa o vão até o topo da linha seguinte, e a de cima começa ali. O fio
 * é translúcido, então duas metades sobrepostas desenham um toco mais escuro.
 *
 * Três estados e não dois: `queued` é a próxima linha pendente que está FECHADA —
 * contorno ink sem preenchimento significa "existe e é sua, mas ainda não
 * aconteceu". Preenchido = agora.
 */
function LegBubble({
  n,
  active,
  queued,
  wireUp,
  wireDown,
}: {
  readonly n: number;
  readonly active: boolean;
  readonly queued: boolean;
  readonly wireUp: boolean;
  readonly wireDown: boolean;
}) {
  let state = '';
  if (active) {
    state = ' is-active';
  } else if (queued) {
    state = ' is-queued';
  }
  return (
    <div className="fpl-node">
      {wireUp && <span className="fpl-wire is-up" aria-hidden="true" />}
      {wireDown && <span className="fpl-wire is-down" aria-hidden="true" />}
      <span className={`fpl-bubble${state}`}>{n}</span>
    </div>
  );
}

/**
 * Uma ponta do trecho resumido: glifo + IATA, e só.
 *
 * A cidade saía daqui até a busca REMOTA de aeroportos entrar: o rótulo dela é o
 * endereço inteiro do provider ("Aeroporto Internacional de Guarulhos - Rodovia
 * Hélio Smidt - Aeroporto, Guarulhos - SP, …"), e duas pontas assim empurram a DATA
 * para fora da linha — some justamente o que o resumo existe para provar. Reduzir
 * esse texto a uma cidade é parsear prosa de fornecedor, que é a suposição que
 * quebrou (o parse antigo cortava no `·` e esses rótulos não têm nenhum).
 *
 * O IATA sozinho identifica o aeroporto sem ambiguidade — inclusive a que a cidade
 * NÃO resolve (GRU × CGH são os dois "São Paulo"). O rótulo por extenso continua
 * onde se escolhe: no combobox da linha aberta.
 */
function LegEndpoint({
  iata,
  endpoint,
  undefinedText,
}: {
  readonly iata: string;
  readonly endpoint: 'origin' | 'destination';
  readonly undefinedText: string;
}) {
  if (!iata.trim()) return <span className="fpl-sum-empty">{undefinedText}</span>;
  const Glyph = endpoint === 'origin' ? AirplaneTakeoffIcon : AirplaneLandingIcon;
  return (
    <span className="fpl-sum-end">
      <Glyph size={14} />
      <span className="fpl-sum-iata">{iata}</span>
    </span>
  );
}

/**
 * A linha COLAPSADA — o trecho inteiro numa frase, e ela é um botão: clicar reabre.
 *
 * Ela tem de PROVAR o que foi escolhido, então usa a régua do campo aberto (glifo +
 * IATA em cada ponta, mais a data) em vez de um resumo mais pobre que os campos que
 * substitui: uma linha resumida pobre parece estado de erro, não confirmação. Com
 * moldura e elevação ela vira a mesma espécie visual das pílulas da linha aberta —
 * que é a verdade: é o mesmo dado, encapsulado.
 *
 * O QUEM fica de fora de propósito: é igual em todos os trechos, e informação
 * idêntica repetida N vezes é ruído, não confirmação.
 */
function LegSummary({
  leg,
  transfer,
  readOnly,
  onEdit,
  copy,
  locale,
}: {
  readonly leg: PlannerLeg;
  readonly transfer: boolean;
  /** Card congelado (interação já resolvida): a linha continua LEGÍVEL e deixa de
   *  ser alvo. Nem `button` desabilitado — não existe ação a anunciar, e o conteúdo
   *  (rota + data) é o que interessa. Sem isto dava pra expandir um card resolvido e
   *  cair num formulário de campos mortos. */
  readonly readOnly: boolean;
  readonly onEdit: () => void;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
}) {
  const undefinedText = copy.multiLegSummaryUndefined;
  const Row = readOnly ? 'div' : 'button';
  return (
    <Row
      {...(readOnly ? {} : { type: 'button' as const, onClick: onEdit })}
      className={`fpl-leg-sum${readOnly ? ' is-static' : ''}`}
    >
      <LegEndpoint iata={leg.origin} endpoint="origin" undefinedText={undefinedText} />
      {transfer && <span className="fpl-transfer">{copy.multiLegTransfer}</span>}
      <ArrowRightIcon size={14} aria-hidden />
      <LegEndpoint iata={leg.destination} endpoint="destination" undefinedText={undefinedText} />
      {/* A data segue a rota em vez de ir pra borda: justificada, a linha lia como
          tabela de duas colunas e o vão do meio virava buraco. Só entra quando
          existe — "a definir" três vezes na mesma linha para de informar. */}
      {leg.date && <span className="fpl-sum-date">· {fmtWeekdayDayMonth(leg.date, locale)}</span>}
      {/* O lápis responde a uma intenção (o hover) em vez de se anunciar sozinho, e
          o que ele reforça é O QUE o clique faz — a linha já comunica que é
          clicável pelo próprio hover. `opacity` e não montagem condicional: o glifo
          ocupa os 14px desde sempre, senão a linha escorrega no primeiro hover.
          Não é botão: a linha é o botão, e botão dentro de botão é defeito. */}
      {!readOnly && (
        <span className="fpl-sum-edit" aria-hidden="true">
          <PencilSimpleIcon size={14} aria-hidden />
        </span>
      )}
    </Row>
  );
}

/** A linha ABERTA: as duas praças e a data, com o anel de atenção no campo da
 *  vez. O QUEM saiu das linhas (#2136, veredito do debate de 2026-08-23): o
 *  valor é da VIAGEM, e o campo único mora no cabeçalho do chamador — repetição
 *  por linha prometia um escopo por-perna que o produto não tem. */
function LegOpen({
  leg,
  focus,
  readOnly,
  searchAirports,
  suggestions,
  originGlow = false,
  dateField,
  showLabels = true,
  placeSheet = false,
  placeGuide,
  onLeg,
  copy,
  locale,
  airportLabel,
}: {
  readonly leg: PlannerLeg;
  readonly focus: LegField | null;
  readonly readOnly: boolean;
  readonly searchAirports: (term: string) => Promise<SearchSelectOption[]>;
  readonly suggestions: LegAirportSuggestions;
  /** Rótulos das colunas SÓ na primeira linha (#2136, régua do desenho de
   *  2026-08-15): as colunas são as mesmas em todas — a primeira nomeia, as de
   *  baixo herdam pelo alinhamento. O nome acessível de cada campo fica.
   *  No palco ESTREITO não há colunas a herdar (ver `MultiLegBuilder.stacked`). */
  readonly showLabels?: boolean;
  /** A origem desta linha acabou de ser HERDADA do destino anterior (#2136): o
   *  brilho de chegada de 2,2s — preenchimento instantâneo passa despercebido, e
   *  a pessoa não entende de onde a origem veio. */
  readonly originGlow?: boolean;
  /** O QUANDO da linha como CONTROLE (#2136): presente, substitui o espelho —
   *  é o popover de datas da perna, sem saia (o clique no dia decide). Ausente,
   *  o espelho de sempre (o editor da bancada segue com a grade inline). */
  readonly dateField?: ReactNode;
  /** DE/PARA abrem em FOLHA de tela cheia (#2653) — o palco estreito do
   *  layout aberto; ver `RouteField.sheet`. */
  readonly placeSheet?: boolean;
  /** Ver `MultiLegBuilder.placeGuide` — já com o id desta linha aplicado;
   *  `undefined` quando o form não guia. */
  readonly placeGuide: (field: 'origin' | 'destination') => PlaceGuide | undefined;
  readonly onLeg: (patch: Partial<PlannerLeg>) => void;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
  readonly airportLabel: ((iata: string) => string) | undefined;
}) {
  // `placeholder` fica FORA do comum: ele é o pedido da PONTA ("De onde parte?" ×
  // "Para onde?"), e um trecho do multi é uma rota — as duas praças pedem o mesmo que
  // as do corpo binário. O resto (prompt, loading, vazio) descreve a BUSCA, que é a
  // mesma nas duas pontas.
  const common = {
    disabled: readOnly,
    promptText: copy.airportSearchPrompt,
    emptyText: copy.noAirports,
    onSearch: searchAirports,
    sheet: placeSheet,
    copy,
    airportLabel,
  };
  return (
    <div className="fpl-leg-open">
      <div
        className={`fpl-leg-field${focus === 'origin' ? ' is-wanted' : ''}${
          // Sem rótulo a pílula começa no topo do campo: a varredura perde a
          // reserva de 20px do rótulo (`.fpl-glow-nolabel`), senão cobre só a
          // metade de baixo enquanto o anel acende a pílula inteira.
          originGlow ? ` fpl-inherit-glow${showLabels ? '' : ' fpl-glow-nolabel'}` : ''
        }`}
      >
        <RouteField
          {...common}
          label={copy.from}
          placeholder={copy.fromEmpty}
          endpoint="origin"
          value={leg.origin}
          selectedLabel={leg.originLabel}
          hideLabel={!showLabels}
          {...(suggestions.origin ? { sections: suggestions.origin } : {})}
          {...placeGuide('origin')}
          onChange={(v, label) => {
            onLeg({ origin: v, originLabel: label });
          }}
        />
      </div>
      <div className={`fpl-leg-field${focus === 'destination' ? ' is-wanted' : ''}`}>
        <RouteField
          {...common}
          label={copy.to}
          placeholder={copy.toEmpty}
          endpoint="destination"
          value={leg.destination}
          selectedLabel={leg.destinationLabel}
          hideLabel={!showLabels}
          {...(suggestions.destination ? { sections: suggestions.destination } : {})}
          {...placeGuide('destination')}
          onChange={(v, label) => {
            onLeg({ destination: v, destinationLabel: label });
          }}
        />
      </div>
      {/* Presente, o QUANDO é CONTROLE (o popover da perna, #2136); ausente, o
          espelho de sempre — a grade inline abaixo é o controle (o editor da
          bancada), e o anel entra quando é a data que falta. */}
      {dateField ?? (
        <div
          className={`genui2 fpl-field fpl-field-when bg-transparent${focus === 'date' ? ' is-wanted' : ''}`}
        >
          {showLabels && <span className="fpl-flabel">{copy.when}</span>}
          <span className={whenMirrorClass(Boolean(leg.date))}>
            {leg.date ? fmtWeekdayDayMonth(leg.date, locale) : copy.whenEmpty}
          </span>
        </div>
      )}
    </div>
  );
}

/** A frase do status da lista: nomeia o trecho e o campo que faltam (#5285) ou
 *  diz que os trechos estão prontos. */
function LegsNote({
  legs,
  copy,
}: {
  readonly legs: readonly PlannerLeg[];
  readonly copy: FlightPlannerCopy;
}) {
  const pending = firstPendingLeg(legs);
  return pending
    ? copy.multiLegMissing({ field: pending.field, n: pending.index + 1 })
    : copy.multiLegReady({ count: legs.length });
}

/**
 * O nó `Adicionar trecho` — o último da timeline: mesma coluna das bolhas, o fio
 * subindo até ele. Tracejado diz "ainda não é real" sem precisar de uma segunda
 * cor. O rótulo é o `VOLL/Button` ghost de 28px do frame (#2362) — `sm` do DS —,
 * não mais a linha inteira como alvo.
 *
 * Sem o guia, DESABILITADO até todo trecho existente fechar (rota + data): sem a
 * data do anterior, o piso encadeado do trecho novo cai em HOJE — a grade abre
 * meses antes da viagem e aceita uma data que o `clearDatesBelow` apaga depois;
 * a frase do rodapé diz o que falta. No guia ele não trava calado: pergunta o
 * campo vazio (`pendingAsk`), a mesma régua do Buscar (#3844, #5285).
 */
function AddLegNode({
  complete,
  guide,
  onAdd,
  spacer,
  copy,
}: {
  readonly copy: FlightPlannerCopy;
  readonly complete: boolean;
  readonly guide: Pick<PlannerGuide, 'pendingAsk'> | undefined;
  readonly onAdd: () => void;
  /** A coluna da lixeira das linhas de cima: sem ela o alvo fica 32px mais largo
   *  que elas e a borda direita serrilha. */
  readonly spacer: boolean;
}) {
  const ask = complete ? null : (guide?.pendingAsk(true) ?? null);
  return (
    <div className={`fpl-leg-add${complete || ask ? '' : ' is-blocked'}`}>
      <div className="fpl-node">
        <span className="fpl-wire is-up" aria-hidden="true" />
        <span className="fpl-bubble is-next">
          <PlusIcon size={12} aria-hidden />
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="genui2 bg-transparent"
        disabled={!complete && !ask}
        onClick={ask ?? onAdd}
      >
        {copy.multiLegAddLeg}
      </Button>
      {spacer && <span className="fpl-leg-spacer" aria-hidden="true" />}
    </div>
  );
}

/**
 * Construtor de trechos do modo `multi_leg`: uma linha por perna, numerada e ligada
 * por um fio, e **só a que está sendo editada fica aberta**.
 *
 * O modelo de interação: a VEZ avança sozinha (escolheu a origem, o destino pede
 * a vez; escolheu o destino, a data) — só o anel de atenção, nenhum campo abre
 * sem gesto — com criação EXPLÍCITA de trecho. A
 * alternativa — a linha seguinte nascer aberta — faria terminar a viagem ser um ato
 * de destruição: sempre sobraria uma linha vazia pra apagar, e o X passaria a
 * significar "terminei" em vez de "errei".
 *
 * Controlado: o card é dono de `legs`, de quem está aberto e de qual campo pede a
 * vez, porque o calendário abaixo também mexe nisso.
 */
export function MultiLegBuilder({
  legs,
  legIds,
  editingLegId,
  legFocus,
  queuedLegId,
  cta,
  onLeg,
  onEdit,
  onAdd,
  onRemove,
  searchAirports,
  suggestions = {},
  readOnly = false,
  alwaysOpen = false,
  dateField,
  glowLegId = null,
  placeSheet = false,
  stacked = false,
  guide,
  copy,
  locale,
  airportLabel,
}: {
  /** Os textos do form (`FlightPlannerCopy`) e o locale das datas. */
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
  /** Ver `PlannerFormProps.airportLabel`. */
  readonly airportLabel?: ((iata: string) => string) | undefined;
  readonly legs: PlannerLeg[];
  /** Keys estáveis por trecho (paralelo a `legs`), mantidas pelo card. */
  readonly legIds: string[];
  readonly editingLegId: string | null;
  readonly legFocus: LegField | null;
  /** A próxima linha pendente que NÃO é a aberta — uma marca só, senão "próxima"
   *  para de querer dizer alguma coisa. */
  readonly queuedLegId: string | null;
  /**
   * **A saída da viagem, injetada — hoje só a FAIXA da bancada a usa.** No CARD a
   * saída desceu para o rodapé (`CardFooter`, régua `lg` de 36px), padronizando a
   * confirmação com as abas binárias — decisão do UX Review Pacote 2 (M2), que
   * reverteu deliberadamente o desenho anterior (CTA na linha do status): o rótulo
   * fala da VIAGEM e o `disabled` até a lista fechar dizem que o gesto não é da
   * perna aberta.
   *
   * Nó e não callback: o rótulo do CTA é do CARD (`submitLabel` do host, com o
   * default do i18n) e o gate também, então um par `onSubmit`+`label`
   * duplicaria as duas decisões aqui.
   *
   * Ausente = quem hospeda tem fecho PRÓPRIO — o card (rodapé) e o painel de edição
   * da faixa (`Cancelar`/`Atualizar`). Card congelado (`readOnly`) também não recebe.
   */
  readonly cta?: ReactNode;
  /** DE/PARA de cada linha em FOLHA de tela cheia (#2653) — ver `LegOpen`. */
  readonly placeSheet?: boolean;
  /** O palco ESTREITO empilha cada linha em COLUNA (`flight_planner.css`,
   *  `max-width: 640px`): sem colunas alinhadas, "a primeira nomeia e as de
   *  baixo herdam" deixa o 2º trecho sem rótulo nenhum — então aqui TODA linha
   *  aberta rotula Origem/Destino/Quando, como o print mobile da #2647 (#2773). */
  readonly stacked?: boolean;
  readonly onLeg: (index: number, patch: Partial<PlannerLeg>) => void;
  readonly onEdit: (id: string) => void;
  readonly onAdd: () => void;
  readonly onRemove: (index: number) => void;
  readonly searchAirports: (term: string) => Promise<SearchSelectOption[]>;
  /** Ver `LegAirportSuggestions`. Default `{}` — nenhuma ponta sugere. */
  readonly suggestions?: LegAirportSuggestions;
  readonly readOnly?: boolean;
  /**
   * **Toda linha fica ABERTA, sempre** (#2136, port da decisão de 2026-08-15 do
   * desenho): no card do chat e na tela determinística cada trecho mostra os
   * campos o tempo todo — o colapso nasceu pro calendário inline, onde altura
   * era o orçamento; com o QUANDO da linha em popover, o formulário aberto É a
   * tela. `editingLegId`/`legFocus` continuam mandando no anel de atenção e nos
   * popovers (uma linha pede a vez por reforço, não por exclusividade). O
   * default `false` preserva o colapso onde ele segue certo: o editor multi da
   * bancada, num popover onde altura ainda é o orçamento — e o card CONGELADO,
   * onde os resumos são a leitura.
   */
  readonly alwaysOpen?: boolean;
  /** O QUANDO por linha como CONTROLE — ver a prop homônima do `LegOpen`.
   *  Função porque cada linha precisa do SEU popover (piso encadeado, data
   *  própria). */
  readonly dateField?: (index: number) => ReactNode;
  /** A linha cuja origem o sistema ACABOU de herdar (#2136) — ganha o brilho de
   *  chegada de 2,2s no campo DE. */
  readonly glowLegId?: string | null;
  /** O preenchimento GUIADO da página (`useGuidedFill`): `place` abre o DE/PARA
   *  da linha da vez como continuação do pick anterior (o anel segue sendo o
   *  `legFocus` de sempre) e `pendingAsk` faz o `Adicionar` perguntar o campo
   *  que falta em vez de ficar apagado (#5285). Ausente ⇒ nenhum campo abre por
   *  conta do form e o `Adicionar` segue travado até fechar os trechos. */
  readonly guide?: Pick<PlannerGuide, 'place' | 'pendingAsk'>;
}) {
  const complete = legsComplete(legs);
  /* A lixeira só existe a partir da TERCEIRA linha, e aí em todas: com exatamente
     dois trechos, apagar viraria one-way por acidente, e a coluna nem é reservada
     (era vão morto). Com três ou mais, qualquer linha pode sair porque sempre
     sobram dois. */
  const canRemove = legs.length > 2 && !readOnly;
  /* O `Adicionar` é o último NÓ da timeline: com ele na tela, o último trecho
     também desce até ele — senão a coluna fica vazia ao lado dos campos dele, que
     no estreito (#3636) é a altura de três pílulas. */
  const canAdd = legs.length < PLANNER_MAX_LEGS && !readOnly;

  return (
    <div className="fpl-multi">
      <div className="fpl-legs">
        {legs.map((leg, i) => {
          const id = legIds[i] ?? String(i);
          const editing = id === editingLegId;
          const open = editing || alwaysOpen;
          return (
            <div key={id} className="fpl-leg" data-leg-index={i}>
              <LegBubble
                n={i + 1}
                active={editing}
                queued={id === queuedLegId}
                wireUp={i > 0}
                wireDown={i < legs.length - 1 || canAdd}
              />
              {open ? (
                /* O anel e o popover são só da linha em edição — duas linhas
                   pedindo atenção leem como pedidos concorrentes. Nenhum campo
                   abre sozinho: o anel (`focus`) só aponta quem pede a vez. */
                <LegOpen
                  key={id}
                  leg={leg}
                  focus={editing ? legFocus : null}
                  readOnly={readOnly}
                  searchAirports={searchAirports}
                  suggestions={suggestions}
                  originGlow={id === glowLegId}
                  // Nas linhas sempre abertas, só a PRIMEIRA nomeia as colunas;
                  // no colapso (uma aberta por vez) a aberta é sempre nomeada; em
                  // coluna (`stacked`) não há coluna a herdar — todas nomeiam.
                  showLabels={!alwaysOpen || i === 0 || stacked}
                  {...(dateField ? { dateField: dateField(i) } : {})}
                  placeSheet={placeSheet}
                  placeGuide={(field) => guide?.place(id, field)}
                  onLeg={(patch) => {
                    onLeg(i, patch);
                  }}
                  copy={copy}
                  locale={locale}
                  airportLabel={airportLabel}
                />
              ) : (
                <LegSummary
                  leg={leg}
                  transfer={isTransfer(legs, i)}
                  readOnly={readOnly}
                  onEdit={() => {
                    onEdit(id);
                  }}
                  copy={copy}
                  locale={locale}
                />
              )}
              {/* A lixeira é o `VOLL/Button-Icon` de 24px do frame (#2362): ghost
                  `icon-xs` do DS. `mb-1.5` centra os 24 na pílula de 36 da linha
                  (a linha alinha pelo pé). `genui2` + `bg-transparent` pela mesma
                  razão dos demais botões do planner fora da raiz de tema. */}
              {canRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="genui2 fpl-leg-remove mb-1.5 bg-transparent"
                  aria-label={copy.multiLegRemoveLeg}
                  onClick={() => {
                    onRemove(i);
                  }}
                >
                  <TrashIcon aria-hidden />
                </Button>
              )}
            </div>
          );
        })}

        {/* `Adicionar trecho` é o último NÓ da timeline: mesma coluna das bolhas,
            o fio subindo até ele. Tracejado diz "ainda não é real" sem precisar de
            uma segunda cor. O rótulo é o `VOLL/Button` ghost de 28px do frame
            (#2362) — `sm` do DS —, não mais a linha inteira como alvo.
            DESABILITADO até todo trecho existente fechar (rota + data): sem a data
            do anterior, o piso encadeado do trecho novo cai em HOJE — a grade abre
            meses antes da viagem e aceita uma data que o `clearDatesBelow` apaga
            depois. A frase do rodapé já diz o que falta. */}
        {canAdd && (
          <AddLegNode
            complete={complete}
            guide={guide}
            onAdd={onAdd}
            spacer={canRemove}
            copy={copy}
          />
        )}
      </div>

      {/* A linha do STATUS da lista — só a frase. No card a saída da viagem mora no
          `CardFooter` (UX Review Pacote 2, M2), igual às outras duas abas; o `cta`
          só chega aqui quando a FAIXA da bancada injeta o dela (ver a nota da prop). */}
      <div className="fpl-legs-foot">
        <span className="fpl-legs-note">
          <LegsNote legs={legs} copy={copy} />
        </span>
        {cta}
      </div>
    </div>
  );
}
